import React, { useEffect, useState } from "react";
import { Search, Eye, CheckCircle, XCircle, Truck } from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";
import { ORDER_API } from "../../../data/api";

const statusStyles = {
  Pending: "border border-yellow-500 text-yellow-600 bg-yellow-50",
  Processing: "border border-blue-500 text-blue-600 bg-blue-50",
  Delivered: "border border-green-500 text-green-600 bg-green-50",
  Rejected: "border border-red-500 text-red-600 bg-red-50",
};

const OrderDashboard = () => {
  const [activeTab, setActiveTab] = useState("new");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [orders, setOrders] = useState({ new: [], processing: [], delivered: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const vendorId = localStorage.getItem("vendorId");
      const response = await axios.get(`${ORDER_API.GET_ALL}/vendor/${vendorId}`);
      const ordersData = response.data;

      setOrders({
        new: ordersData.filter(o => o.orderStatus === "Pending"),
        processing: ordersData.filter(o => o.orderStatus === "Processing"),
        delivered: ordersData.filter(o => o.orderStatus === "Delivered"),
      });
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  // const handleAction = async (orderId, newStatus) => {
  //   try {
  //     const result = await Swal.fire({
  //       title: `Are you sure?`,
  //       text: `You want to mark this order as ${newStatus}?`,
  //       icon: "warning",
  //       showCancelButton: true,
  //       confirmButtonText: "Yes, do it!",
  //       cancelButtonText: "Cancel",
  //     });

  //     if (result.isConfirmed) {
  //       await axios.patch(`${ORDER_API.UPDATE_STATUS}/${orderId}`, {
  //         status: newStatus,
  //       });

  //       Swal.fire({
  //         icon: "success",
  //         title: `Order updated!`,
  //         text: `Order status has been changed to ${newStatus}.`,
  //         timer: 1500,
  //         showConfirmButton: false,
  //       });

  //       fetchOrders(); // Refresh orders
  //     }
  //   } catch (error) {
  //     Swal.fire({
  //       icon: "error",
  //       title: "Error!",
  //       text: "Something went wrong while updating the order.",
  //     });
  //     console.error(error);
  //   }
  // };
const handleAction = async (orderId, newStatus) => {
  const result = await Swal.fire({
    title: "Confirm Action",
    text: `Mark this order as ${newStatus}?`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Yes",
    cancelButtonText: "Cancel",
  });

  if (!result.isConfirmed) return;

  // 🔁 UI STATE UPDATE ONLY
  setOrders(prev => {
    // sab orders ek array me lao
    const allOrders = [
      ...prev.new,
      ...prev.processing,
      ...prev.delivered,
    ];

    // jis order pe click hua
    const updatedOrders = allOrders.map(order =>
      order.orderId === orderId
        ? { ...order, orderStatus: newStatus }
        : order
    );

    return {
      new: updatedOrders.filter(o => o.orderStatus === "Pending"),
      processing: updatedOrders.filter(o => o.orderStatus === "Processing"),
      delivered: updatedOrders.filter(o => o.orderStatus === "Delivered"),
    };
  });

  // ✅ Success Swal ONLY
  Swal.fire({
    icon: "success",
    title: "Success",
    text: `Order marked as ${newStatus}`,
    timer: 1500,
    showConfirmButton: false,
  });
};

  const getFilteredOrders = () => {
    let filtered = orders[activeTab] || [];

    if (search) {
      filtered = filtered.filter(o =>
        (o.items?.[0]?.product?.productName || "")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    }

    if (dateFilter !== "all") {
      filtered = filtered.slice(0, 10); // Placeholder, implement real date filtering if needed
    }

    return filtered;
  };

  const filteredOrders = getFilteredOrders();

  if (loading) return <p>Loading orders...</p>;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Orders Dashboard</h1>
        <p className="text-gray-500 mt-1">
          Quickly track and manage all your orders in one place.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-3">
        {["new", "processing", "delivered"].map(tab => (
          <div
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`cursor-pointer px-4 py-2 rounded-full font-medium transition-all text-sm ${
              activeTab === tab
                ? "bg-gray-800 text-white shadow-sm"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)} Orders
          </div>
        ))}
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mt-4">
        <div className="flex gap-2 flex-wrap">
          {["today", "week", "month", "year", "all"].map(filter => (
            <div
              key={filter}
              onClick={() => setDateFilter(filter)}
              className={`cursor-pointer px-3 py-1 rounded-full text-sm font-medium transition-all ${
                dateFilter === filter
                  ? "bg-gray-800 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 bg-white rounded-xl shadow p-2 w-full md:w-1/3">
          <Search className="text-gray-400" />
          <input
            type="text"
            placeholder="Search product..."
            className="w-full outline-none text-sm"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white shadow rounded-xl p-4 overflow-x-auto mt-4">
        <table className="w-full min-w-max table-auto text-sm">
          <thead>
            <tr className="text-left border-b border-gray-200">
              <th className="p-3 font-medium text-gray-600">Order ID</th>
              <th className="p-3 font-medium text-gray-600">Product</th>
              <th className="p-3 font-medium text-gray-600">Price</th>
              <th className="p-3 font-medium text-gray-600">Date</th>
              <th className="p-3 font-medium text-gray-600">Status</th>
              {/* <th className="p-3 font-medium text-gray-600 text-center">Actions</th> */}
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length > 0 ? (
              filteredOrders.map(order => (
                <tr key={order.orderId} className="border-b border-gray-100">
                  <td className="p-3">{order.orderId}</td>
                  <td className="p-3">{order.items?.[0]?.product?.productName || "N/A"}</td>
                  <td className="p-3">₹{order.items?.[0]?.price || order.finalAmount}</td>
                  <td className="p-3">{new Date(order.createdAt).toLocaleDateString()}</td>
                  <td>
                    <span className={`px-2 py-1 rounded-full ${statusStyles[order.orderStatus] || "bg-gray-100 text-gray-600"}`}>
                      {order.orderStatus}
                    </span>
                  </td>
                  <td className="p-3 flex items-center justify-center gap-2">
                    {/* View Button */}
                    <button
                      className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                      onClick={() => Swal.fire({
                        title: `Order #${order.orderId}`,
                        html: `
                          <b>Product:</b> ${order.items?.[0]?.product?.productName || "N/A"}<br/>
                          <b>Quantity:</b> ${order.items?.[0]?.quantity || 1}<br/>
                          <b>Price:</b> ₹${order.items?.[0]?.price || order.finalAmount}<br/>
                          <b>Date:</b> ${new Date(order.createdAt).toLocaleDateString()}<br/>
                          <b>Status:</b> ${order.orderStatus}
                        `
                      })}
                    >
                      <Eye size={16} />
                    </button>

                    {/* Actions based on tab */}
                    {/* {activeTab === "new" && (
                      <>
                        <button
                          className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                          onClick={() => handleAction(order.orderId, "Processing")}
                        >
                          <CheckCircle size={16} />
                        </button>
                        <button
                          className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                          onClick={() => handleAction(order.orderId, "Rejected")}
                        >
                          <XCircle size={16} />
                        </button>
                      </>
                    )} */}

                    {/* {activeTab === "processing" && (
                      <button
                        className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                        onClick={() => handleAction(order.orderId, "Delivered")}
                      >
                        <Truck size={16} />
                      </button>
                    )} */}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center p-6 text-gray-400 italic">
                  No {activeTab} orders found...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrderDashboard;
